import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Phone,
  RefreshCw,
  Unplug,
  Info,
  ExternalLink
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';

// Meta App Configuration
// Fallback dipakai hanya kalau endpoint /whatsapp-embedded/config tidak terjangkau.
// Sumber kebenaran ada di backend (.env META_APP_ID / META_CONFIG_ID).
const FALLBACK_APP_ID = '4201103220206383';
const FALLBACK_SDK_VERSION = 'v24.0';

const WhatsAppEmbeddedSignup = ({ onSuccess, onError, compact = false, variant = 'full' }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [metaConfig, setMetaConfig] = useState(null);
  const sessionDataRef = useRef(null);

  // Ambil app_id & config_id dari backend
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/whatsapp-embedded/config`)
      .then((res) => {
        if (!cancelled) setMetaConfig(res.data?.config || {});
      })
      .catch(() => {
        if (!cancelled) setMetaConfig({ app_id: FALLBACK_APP_ID, sdk_version: FALLBACK_SDK_VERSION });
      });
    return () => { cancelled = true; };
  }, []);

  // Load Facebook SDK
  useEffect(() => {
    if (!metaConfig) return;

    // Check if SDK already loaded
    if (window.FB) {
      setSdkLoaded(true);
      return;
    }

    // Initialize FB SDK
    window.fbAsyncInit = function() {
      window.FB.init({
        appId: metaConfig.app_id || FALLBACK_APP_ID,
        autoLogAppEvents: true,
        xfbml: true,
        version: metaConfig.sdk_version || FALLBACK_SDK_VERSION
      });
      setSdkLoaded(true);
      console.log('[WhatsApp Embedded] FB SDK initialized');
    };

    // Load SDK script
    (function(d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) return;
      js = d.createElement(s);
      js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      js.async = true;
      js.defer = true;
      fjs.parentNode.insertBefore(js, fjs);
    }(document, 'script', 'facebook-jssdk'));

    // Cleanup
    return () => {
      const script = document.getElementById('facebook-jssdk');
      if (script) {
        // Don't remove - might be used elsewhere
      }
    };
  }, [metaConfig]);

  // Listen for session info from Embedded Signup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
        return;
      }

      try {
        const data = JSON.parse(event.data);
        console.log('[WhatsApp Embedded] Received message:', data);

        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          sessionDataRef.current = data;
          setSessionData(data);
          
          if (data.event === 'FINISH') {
            console.log('[WhatsApp Embedded] Signup completed:', data.data);
            // Data contains waba_id and phone_number_id
          } else if (data.event === 'CANCEL') {
            console.log('[WhatsApp Embedded] Signup cancelled');
            toast.error('Proses signup dibatalkan');
          } else if (data.event === 'ERROR') {
            console.error('[WhatsApp Embedded] Signup error:', data.data);
            toast.error('Terjadi error saat signup');
          }
        }
      } catch (e) {
        // Not JSON or not our message
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Check connection status
  const checkConnectionStatus = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${API}/whatsapp-embedded/status`, {
        params: { user_id: user.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus(response.data);
    } catch (error) {
      console.error('Error checking WhatsApp status:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    checkConnectionStatus();
  }, [checkConnectionStatus]);

  // FB Login callback
  const fbLoginCallback = async (response) => {
    console.log('[WhatsApp Embedded] FB Login response:', response);

    if (response.authResponse) {
      const code = response.authResponse.code;
      
      // Wait a bit for session data to arrive via postMessage
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Get WABA ID and Phone ID from session data ref (avoids stale closure)
      let wabaId = sessionDataRef.current?.data?.waba_id;
      let phoneNumberId = sessionDataRef.current?.data?.phone_number_id;

      // If not available yet, wait a bit more and retry
      if (!wabaId || !phoneNumberId) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        wabaId = sessionDataRef.current?.data?.waba_id;
        phoneNumberId = sessionDataRef.current?.data?.phone_number_id;
      }

      if (!wabaId || !phoneNumberId) {
        console.log('[WhatsApp Embedded] Session data ref:', sessionDataRef.current);
        toast.error('Tidak dapat mendapatkan WABA ID. Silakan coba lagi.');
        setLoading(false);
        return;
      }

      try {
        // Exchange code for token on backend
        const exchangeResponse = await axios.post(`${API}/whatsapp-embedded/exchange-token`, {
          code: code,
          waba_id: wabaId,
          phone_number_id: phoneNumberId,
          user_id: user.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (exchangeResponse.data.success) {
          toast.success('WhatsApp berhasil terhubung!');
          checkConnectionStatus();
          if (onSuccess) onSuccess(exchangeResponse.data);
        }
      } catch (error) {
        console.error('Token exchange error:', error);
        toast.error(error.response?.data?.detail || 'Gagal menghubungkan WhatsApp');
        if (onError) onError(error);
      }
    } else {
      toast.error('Login Facebook dibatalkan');
    }
    
    setLoading(false);
  };

  // Launch Embedded Signup
  const launchWhatsAppSignup = () => {
    if (!sdkLoaded || !window.FB) {
      toast.error('Facebook SDK belum siap. Silakan refresh halaman.');
      return;
    }
    if (!metaConfig?.config_id) {
      toast.error('Konfigurasi Meta belum tersedia. Coba beberapa saat lagi.');
      return;
    }

    setLoading(true);
    setSessionData(null);

    // Launch Facebook login with Embedded Signup config
    window.FB.login((response) => { fbLoginCallback(response); }, {
      config_id: metaConfig.config_id,
      response_type: 'code',
      override_default_response_type: true,
      extras: {
        "version": "v3",
        "setup": {
          "business": {
            "id": null,
            "name": null,
            "email": null,
            "phone": {},
            "website": null,
            "address": {
              "streetAddress1": null,
              "streetAddress2": null,
              "city": null,
              "state": null,
              "zipPostal": null,
              "country": null
            },
            "timezone": null
          },
          "phone": {
            "displayName": null,
            "category": null,
            "description": ""
          },
          "preVerifiedPhone": {
            "ids": null
          },
          "solutionID": null,
          "whatsAppBusinessAccount": {
            "ids": null
          }
        },
        "featureType": "whatsapp_business_app_onboarding",
        "sessionInfoVersion": "3",
        "features": [
          { "name": "app_only_install" }
        ]
      }
    });
  };

  // Disconnect WhatsApp
  const handleDisconnect = async () => {
    if (!window.confirm('Apakah Anda yakin ingin memutuskan koneksi WhatsApp?')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/whatsapp-embedded/disconnect`, {
        params: { user_id: user.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('WhatsApp berhasil diputus');
      checkConnectionStatus();
    } catch (error) {
      toast.error('Gagal memutuskan WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  // Koneksi bermasalah (token mati/dicabut) — banner reconnect satu klik
  const needsReconnect = Boolean(connectionStatus?.needs_reconnect);

  if (variant === 'banner') {
    if (!needsReconnect) return null;
    return (
      <div
        data-testid="wa-reconnect-banner"
        className="flex flex-col gap-3 rounded-xl border border-red-300 bg-red-50 p-4 sm:flex-row sm:items-center dark:border-red-900/60 dark:bg-red-950/40"
      >
        <XCircle className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-red-700 dark:text-red-300">
            WhatsApp terputus — balasan AI ke customer tertahan
          </p>
          <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">
            {connectionStatus?.phone_number ? `Nomor ${connectionStatus.phone_number}: ` : ''}
            izin dari Meta sudah tidak berlaku. Hubungkan ulang, data dan riwayat chat tetap aman.
          </p>
        </div>
        <Button
          onClick={launchWhatsAppSignup}
          disabled={loading || !sdkLoaded}
          className="shrink-0 bg-red-600 text-white hover:bg-red-700"
          data-testid="wa-reconnect-btn"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Hubungkan Ulang
        </Button>
      </div>
    );
  }

  if (connectionStatus?.connected && needsReconnect) {
    const conn = connectionStatus.connection || {};
    return (
      <Card className="border-red-300 bg-red-50 dark:border-red-900/60 dark:bg-red-950/40" data-testid="wa-reconnect-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <XCircle className="h-5 w-5" />
            WhatsApp perlu dihubungkan ulang
          </CardTitle>
          <CardDescription className="text-red-600 dark:text-red-400">
            {conn.phone_display_number || 'Nomor WhatsApp Anda'} tidak bisa mengirim pesan. Pesan masuk tetap
            diterima, tapi balasan AI tertahan sampai koneksi dipulihkan.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {connectionStatus?.last_error && (
            <Alert variant="destructive">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs break-words">{connectionStatus.last_error}</AlertDescription>
            </Alert>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={launchWhatsAppSignup}
              disabled={loading || !sdkLoaded}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="wa-reconnect-card-btn"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Hubungkan Ulang
            </Button>
            <Button variant="outline" size="sm" onClick={checkConnectionStatus} disabled={loading}>
              <RefreshCw className={`mr-1 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Cek Status
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If already connected
  if (connectionStatus?.connected) {
    const conn = connectionStatus.connection;
    
    if (compact) {
      return (
        <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 dark:text-green-400" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-700 dark:text-green-400">WhatsApp Terhubung</p>
            <p className="text-xs text-green-600 dark:text-green-500 truncate">{conn.phone_display_number || conn.verified_name || 'WhatsApp Business'}</p>
            {connectionStatus?.token_never_expires === false && connectionStatus?.token_expires_at && (
              <p className="text-xs text-amber-600 dark:text-amber-400 truncate">
                Token berlaku sampai {new Date(connectionStatus.token_expires_at).toLocaleDateString('id-ID')}
              </p>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <Card className="border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
            <CheckCircle2 className="w-5 h-5" />
            WhatsApp Terhubung
          </CardTitle>
          <CardDescription className="text-green-600 dark:text-green-500">
            Nomor WhatsApp Anda sudah terhubung dengan Nerava
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-white dark:bg-zinc-900 rounded-lg">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
              <FaWhatsapp className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-slate-900 dark:text-zinc-50">
                {conn.phone_display_number || conn.verified_name || 'WhatsApp Business'}
              </p>
              <p className="text-sm text-slate-500 dark:text-zinc-400">
                {conn.verified_name && `Verified: ${conn.verified_name}`}
              </p>
              <div className="flex gap-2 mt-1">
                <Badge variant="outline" className="text-xs">
                  {conn.connection_type === 'embedded_signup' ? 'Embedded Signup' : 'Manual'}
                </Badge>
                {conn.quality_rating && (
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${
                      conn.quality_rating === 'GREEN' ? 'text-green-600' :
                      conn.quality_rating === 'YELLOW' ? 'text-yellow-600' : 'text-red-600'
                    } dark:text-green-400`}
                  >
                    Quality: {conn.quality_rating}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={checkConnectionStatus}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="destructive" 
              size="sm"
              onClick={handleDisconnect}
              disabled={loading}
            >
              <Unplug className="w-4 h-4 mr-1" />
              Putuskan
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Not connected - show signup button
  if (compact) {
    return (
      <div className="space-y-3">
        <Button
          onClick={launchWhatsAppSignup}
          disabled={loading || !sdkLoaded}
          className="w-full bg-green-600 hover:bg-green-700 text-white"
          data-testid="wa-embedded-connect-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Menghubungkan...
            </>
          ) : !sdkLoaded ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Memuat SDK...
            </>
          ) : (
            <>
              <FaWhatsapp className="w-4 h-4 mr-2" />
              Hubungkan dengan Meta
            </>
          )}
        </Button>
        <p className="text-xs text-slate-500 dark:text-zinc-400">Anda akan diarahkan ke halaman Meta untuk mendaftarkan nomor WA Business.</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FaWhatsapp className="w-5 h-5 text-green-500" />
          Hubungkan WhatsApp Business
        </CardTitle>
        <CardDescription>
          Gunakan nomor WhatsApp Business Anda sendiri untuk menerima dan membalas pesan customer
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription>
            <strong>Persyaratan:</strong>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              <li>Akun Facebook Business Manager</li>
              <li>Nomor telepon yang belum terdaftar di WhatsApp</li>
              <li>Akses ke SMS/telepon untuk verifikasi</li>
            </ul>
          </AlertDescription>
        </Alert>

        <div className="flex flex-col gap-3">
          <Button
            onClick={launchWhatsAppSignup}
            disabled={loading || !sdkLoaded}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Menghubungkan...
              </>
            ) : (
              <>
                <FaWhatsapp className="w-5 h-5 mr-2" />
                Hubungkan dengan Facebook
              </>
            )}
          </Button>

          {!sdkLoaded && (
            <p className="text-sm text-amber-600 text-center dark:text-amber-400">
              <Loader2 className="w-4 h-4 inline mr-1 animate-spin" />
              Memuat Facebook SDK...
            </p>
          )}
        </div>

        <div className="pt-4 border-t">
          <p className="text-xs text-slate-500 dark:text-zinc-400">
            Dengan menghubungkan WhatsApp, Anda menyetujui{' '}
            <a href="https://www.whatsapp.com/legal/business-terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
              Ketentuan Layanan WhatsApp Business <ExternalLink className="w-3 h-3 inline" />
            </a>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default WhatsAppEmbeddedSignup;
